import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import Icon from 'dpl/common/components/Icon';
import {
  HELP_CENTER_SERVICE_FEE_PATH,
  PUPPY_TRAINING_PROGRAM_LANDING,
  ROOT_HOST
} from 'dpl/shared/constants/urls';
import { sendAnalyticsEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';

import SelectPaymentTypeLearnMoreDrawer from './SelectPaymentTypeLearnMoreDrawer';
import { SELECT_PAYMENT_MODAL_NAME } from './constants';
import { ensureCurrencyIsPresent } from './util';

function paymentProtectionProvisions(dogPayment, amount) {
  const amountWithCurrencySign = ensureCurrencyIsPresent(
    parseFloat(amount).toFixed(2)
  );
  return [
    {
      icon: 'secure',
      title: 'Payment protection',
      subtitle: 'We make sure you get what you pay for',
      content: `Your ${amountWithCurrencySign} ${
        !dogPayment ? 'deposit' : 'payment'
      } is protected by the Good Dog Guarantee if anything goes wrong.`,
      helpTitle: 'Common scenarios we have covered',
      helpIssues: [
        'Breeder stops responding to you',
        'Breeder doesn’t deliver the dog you were promised',
        'Breeder decides they no longer want to sell you a dog',
        'Breeder does not honor their refund policies',
        dogPayment &&
          'Breeder does not stand by their health guarantee or contract'
      ].filter(Boolean),
      learnMoreLink: HELP_CENTER_SERVICE_FEE_PATH
    },
    {
      icon: 'headset',
      title: 'Support',
      subtitle: 'We have your back',
      content:
        'Our world-class support team, including veterinarians and behaviorists, has your back every step of the way.',
      helpTitle: 'Common issues we have helped with',
      helpIssues: [
        dogPayment && 'Reviewing veterinary records',
        'Resolving disputes with the breeder',
        dogPayment && 'Breeder decides they no longer want to sell you a dog',
        'Logistical and transportation assistance',
        dogPayment && 'Help with behavioral issues',
        'Guidance on puppy care'
      ].filter(Boolean),
      learnMoreLink: HELP_CENTER_SERVICE_FEE_PATH
    },
    dogPayment && {
      icon: 'head-pat',
      title: 'Puppy Training Program',
      subtitle: 'We help you raise a happy, healthy dog',
      content:
        'Get everything you need to navigate puppyhood including how-to videos, checklists, discounts, and more. Plus, weekly coaching calls and direct access to our puppy trainer.',
      helpTitle: 'What you get with the program',
      helpIssues: [
        'How-to videos on house training, socialization, manners, and more',
        'Checklists for your first day, week, and month',
        'Weekly live coaching calls with our puppy trainer',
        'Chat directly with our puppy trainer with any questions',
        'Private Facebook group with other Good Dog owners',
        'Discounts on puppy essentials from Chewy, Rover, BarkBox, and more'
      ],
      learnMoreLink: `${ROOT_HOST}${PUPPY_TRAINING_PROGRAM_LANDING}`
    }
  ].filter(Boolean);
}

const CHANGE_PAYMENT_BUTTON_CTA = 'Change to friends and family payment';

export default function SelectPaymentTypeLearnMore({
  amount,
  onGoBack,
  dogPayment,
  onChangeModal
}) {
  useEffect(() => {
    sendAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_MODAL_VIEWED_BY_BUYER, {
      view: ANALYTICS_VIEWS.PROTECTION_SUPPORT_LEARN_MORE,
      view_feature: ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE
    });
  }, []);

  function handleChangePaymentClick() {
    onChangeModal(SELECT_PAYMENT_MODAL_NAME.PAYMENT_PROTECTION);
    sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: CHANGE_PAYMENT_BUTTON_CTA,
      view: ANALYTICS_VIEWS.BUYER_PAYMENT_SUMMARY
    });
  }

  const HEADER_COPY = dogPayment
    ? 'What do you get with Payment Protection & Support?'
    : 'What do you get with Deposit Protection & Support?';

  return (
    <div className="SelectPaymentTypeLearnMore pv4 ph4 pv10-sm ph16-sm overflow-y-auto no-scrollbars">
      {onGoBack && (
        <button type="button" onClick={onGoBack}>
          <Icon
            name="fat-arrow-left"
            className="dib"
            height="30px"
            width="30px"
          />
        </button>
      )}
      <h4 className="f4 f6-sm tc mb8 lh-normal mt4-sm">{HEADER_COPY}</h4>
      <div>
        {paymentProtectionProvisions(dogPayment, amount).map(provision => (
          <div key={provision.title} className="mb4">
            <SelectPaymentTypeLearnMoreDrawer {...provision} />
          </div>
        ))}
      </div>
      <button
        className="flex justify-center w-100 font-16 fw-medium midnight mt6 mb3 mb0-sm mt8-sm"
        type="button"
        onClick={handleChangePaymentClick}
      >
        {CHANGE_PAYMENT_BUTTON_CTA}
      </button>
    </div>
  );
}

SelectPaymentTypeLearnMore.propTypes = {
  amount: PropTypes.number.isRequired,
  onGoBack: PropTypes.func,
  dogPayment: PropTypes.bool.isRequired,
  onChangeModal: PropTypes.func
};

SelectPaymentTypeLearnMore.defaultProps = {
  onGoBack: null,
  onChangeModal: null
};
